import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "./store";
import { db } from "../firebase";
import { doc, onSnapshot, collection } from "firebase/firestore";

/*──────────────────────────────────────────────
  TYPES
──────────────────────────────────────────────*/
interface DailyRow {
  date: string;
  totalEarned: number;
  isPaidOut: boolean;
}

interface PayoutState {
  totalReverse: number;
  reverseStatus: "pending" | "completed" | "failed";
  pendingBalance: number;
  estimatedBalance: number;
  paidOut: number;

  awardPrize: number;
  awardsRunnersUp: number;
  todayBalance: number;

  credits: number;
  refundCredits: number;
  debtCredits: number;
  referrals: number;

  lastUpdated?: string | null;

  dailyRows: DailyRow[];

  isProcessed: boolean;
  isProcessing: boolean;
  isPending: boolean;

  loading: boolean;
}

/*──────────────────────────────────────────────
  INITIAL STATE
──────────────────────────────────────────────*/
const initialState: PayoutState = {
  pendingBalance: 0,
  estimatedBalance: 0,
  paidOut: 0,

  awardPrize: 0,
  awardsRunnersUp: 0,
  todayBalance: 0,

  credits: 0,
  refundCredits: 0,
  debtCredits: 0,
  referrals: 0,

  lastUpdated: null,

  dailyRows: [],

  isProcessed: false,
  isProcessing: false,
  isPending: false,

  loading: false,
  totalReverse: 0,
  reverseStatus: "pending",
};

/*──────────────────────────────────────────────
  SLICE
──────────────────────────────────────────────*/
const payoutsSlice = createSlice({
  name: "payouts",
  initialState,
  reducers: {
    setPayoutData: (state, action: PayloadAction<Partial<PayoutState>>) => {
      return { ...state, ...action.payload, loading: false };
    },
    setPayoutLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    resetPayoutData: () => initialState,
  },
});

export const { setPayoutData, setPayoutLoading, resetPayoutData } =
  payoutsSlice.actions;

export default payoutsSlice.reducer;

/*──────────────────────────────────────────────
  THUNK — FULL HOST PAYOUT LISTENER
──────────────────────────────────────────────*/
export const listenToHostPayout =
  (userId: string) => (dispatch: AppDispatch) => {
    if (!userId) return;

    dispatch(setPayoutLoading(true));

    const rootRef = doc(db, "payouts", userId);

    /* ─────────── ROOT DOC LISTENER ─────────── */
    const unsubRoot = onSnapshot(
      rootRef,
      (snap) => {
        if (!snap.exists()) {
          dispatch(resetPayoutData());
          return;
        }

        const data = snap.data();

        dispatch(
          setPayoutData({
            pendingBalance: data.pendingBalance || 0,
            estimatedBalance: data.estimatedBalance || 0,
            paidOut: data.paidOut || 0,

            awardPrize: data.awardPrize || 0,
            awardsRunnersUp: data.awardsRunnersUp || 0,
            todayBalance: data.todayBalance || 0,

            credits: data.credits || 0,
            refundCredits: data.refundCredits || 0,
            debtCredits: data.debtCredits || 0,
            referrals: data.referrals || 0,

            isProcessed: data.isProcessed ?? false,
            isProcessing: data.isProcessing ?? false,
            isPending: data.isPending ?? false,

            lastUpdated: data.lastUpdated?.toDate
              ? data.lastUpdated.toDate().toISOString()
              : null,
          })
        );
      },
      (error) => {
        console.error("❌ Root payouts listener failed:", error);
        dispatch(resetPayoutData());
      }
    );

    /* ─────────── DAILY SUBCOLLECTION LISTENER ─────────── */
    const dailyRef = collection(db, "payouts", userId, "dailyPayouts");

    const unsubDaily = onSnapshot(
      dailyRef,
      (snap) => {
        const dailyRows: DailyRow[] = [];

        snap.forEach((docSnap) => {
          const row = docSnap.data();

          dailyRows.push({
            date: docSnap.id,
            totalEarned: row.totalEarned || 0,
            isPaidOut: row.isPaidOut ?? false,
          });
        });

        dispatch(setPayoutData({ dailyRows }));
      },
      (error) => {
        console.error("❌ Error in dailyPayouts listener:", error);
      }
    );

    return () => {
      unsubRoot();
      unsubDaily();
    };
  };


  /*──────────────────────────────────────────────
  THUNK — SIMPLE USER PAYOUT LISTENER (FRONT SIDE)
──────────────────────────────────────────────*/
export const listenToUserPayouts =
  (userId: string) => (dispatch: AppDispatch) => {
    if (!userId) return () => {}; // 👈 ALWAYS return a cleanup function

    const ref = doc(db, "payouts", userId);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();

          dispatch(
            setPayoutData({
              credits: data.credits || 0,
              refundCredits: data.refundCredits || 0,
              debtCredits: data.debtCredits || 0,
              pendingBalance: data.pendingBalance || 0,
              estimatedBalance: data.estimatedBalance || 0,
              paidOut: data.paidOut || 0,

              // ⭐ CORRECT FIELDS
              totalReverse: data.totalReverse || 0,
              reverseStatus: data.reverseStatus || "pending",
              lastUpdated: data.lastUpdated?.toDate
                ? data.lastUpdated.toDate().toISOString()
                : null,
            })
          );
        } else {
          dispatch(
            setPayoutData({
              credits: 0,
              refundCredits: 0,
              debtCredits: 0,
              pendingBalance: 0,
              estimatedBalance: 0,
              paidOut: 0,

              // ⭐ CORRECT FIELDS DEFAULTS
              totalReverse: 0,
              reverseStatus: "pending",
              lastUpdated: null,
            })
          );
        }
      },
      (err) => {
        console.error("❌ listenToUserPayouts error:", err);
      }
    );

    return unsub; // 👈 ALWAYS return the real cleanup function
  };
